import { useEffect, useState } from "react";
import { Menu, Phone, ShoppingBag, X, CalendarCheck } from "lucide-react";
import { ORDER_URL, BOOK_URL, PHONE_TEL } from "./data";

const LINKS = [
  { label: "Home", href: "#home" },
  { label: "About", href: "#about" },
  { label: "Menu", href: "#menu" },
  { label: "Reviews", href: "#reviews" },
  { label: "Contact", href: "#contact" },
];

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <header
      data-testid="main-navigation"
      className={`fixed top-0 left-0 right-0 z-50 transition-[background-color,border-color,box-shadow] duration-300 ${
        scrolled || open
          ? "glass-panel border-b border-[#C97B5F]/20 shadow-[0_8px_30px_rgba(0,0,0,0.5)]"
          : "bg-transparent border-b border-transparent"
      }`}
    >
      <div className="mx-auto flex h-16 max-w-6xl items-center justify-between px-4 sm:px-6">
        <a href="#home" data-testid="nav-brand-logo" className="flex items-center" onClick={() => setOpen(false)}>
          <span className="font-cinzel text-lg font-bold tracking-wide text-[#2A211A]">
            Curry <span className="text-gold-gradient">Story</span>
          </span>
        </a>

        <nav className="hidden items-center gap-7 md:flex" aria-label="Primary">
          {LINKS.map((l) => (
            <a
              key={l.href}
              href={l.href}
              data-testid={`nav-link-${l.label.toLowerCase()}`}
              className="text-sm font-medium text-stone-600 transition-colors duration-200 hover:text-[#8A3324]"
            >
              {l.label}
            </a>
          ))}
        </nav>

        <div className="hidden items-center gap-3 md:flex">
          <a
            href={PHONE_TEL}
            data-testid="nav-call-phone-button"
            className="flex h-10 w-10 items-center justify-center rounded-full border border-[#C97B5F]/40 text-[#8A3324] transition-colors duration-200 hover:bg-[#D4A359]/10"
            aria-label={`Call Curry Story`}
          >
            <Phone size={16} />
          </a>
          <a
            href={ORDER_URL}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="nav-order-online-button"
            className="flex items-center gap-2 rounded-full bg-gradient-to-r from-[#E86A24] to-[#B82E1F] px-5 py-2.5 text-xs font-bold uppercase tracking-wider text-white shadow-lg shadow-[#E86A24]/30 transition-transform duration-200 hover:scale-[1.03]"
          >
            <ShoppingBag size={14} /> Order Online
          </a>
        </div>

        <button
          data-testid="nav-mobile-menu-toggle"
          className="flex h-10 w-10 items-center justify-center rounded-full border border-[#C97B5F]/30 text-[#3A2C1E] md:hidden"
          onClick={() => setOpen((v) => !v)}
          aria-label="Toggle menu"
        >
          {open ? <X size={18} /> : <Menu size={18} />}
        </button>
      </div>

      {open && (
        <div data-testid="nav-mobile-menu" className="glass-panel border-t border-[#C97B5F]/15 px-4 pb-5 pt-3 md:hidden">
          <nav className="flex flex-col gap-1" aria-label="Mobile">
            {LINKS.map((l) => (
              <a
                key={l.href}
                href={l.href}
                data-testid={`mobile-nav-link-${l.label.toLowerCase()}`}
                onClick={() => setOpen(false)}
                className="rounded-lg px-3 py-2.5 text-sm font-medium text-stone-600 transition-colors duration-200 hover:bg-[#D4A359]/10 hover:text-[#8A3324]"
              >
                {l.label}
              </a>
            ))}
          </nav>
          <div className="mt-4 flex gap-2">
            <a
              href={ORDER_URL}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="mobile-nav-order-online-button"
              className="flex flex-1 items-center justify-center gap-2 rounded-full bg-gradient-to-r from-[#E86A24] to-[#B82E1F] py-3 text-xs font-bold uppercase tracking-wider text-white"
            >
              <ShoppingBag size={14} /> Order Online
            </a>
            <a
              href={BOOK_URL}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="nav-book-table-button"
              className="flex flex-1 items-center justify-center gap-2 rounded-full border border-[#C97B5F] py-3 text-xs font-bold uppercase tracking-wider text-[#8A3324]"
            >
              <CalendarCheck size={14} /> Book a Table
            </a>
          </div>
        </div>
      )}
    </header>
  );
}
